import { Box, Button, Grid, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { parseDateToLocal } from '../../../../shared/utils';
import { useDispatch, useSelector } from 'react-redux';
import { getCustomer } from '../../../../store/customers/actionCreators';
import { RootState } from '../../../../store';
import { useParams } from 'react-router-dom';
import LoadingSpinner from '../../../Shared/LoadingSpinner';
import {
  clearDeviceActionStatus,
  linkDeviceToOwner,
} from '../../../../store/devices/actionCreators';
import { DeviceActionType } from '../../../../shared/constants';
import NewModal from '../../../Shared/NewModal';
import HubAssignment from '../../../Dashboard/AddNewController/components/Patient/HubAssignment';
import { StatusMessage } from '../../../Hubs/HubDetails/AssignHubController/components/StatusMessage';
import { ReactComponent as UpdateSwSuccessIcon } from '../../../../images/icons/update_sw_success.svg';
import { ReactComponent as HubAssignFailedIcon } from '../../../../images/icons/hub_assign_failed.svg';
import { dimGrey, lightBlack } from '../../../../mainTheme';

const baseStyle = {
  color: lightBlack,
  fontWeight: 700,
  lineHeight: '24px',
  letterSpacing: '0.2px',
  fontSize: '16px',
};

const contentStyle = {
  ...baseStyle,
  color: dimGrey,
  fontWeight: 500,
  paddingBottom: 2,
};

const ASSIGN_STEPS = {
  ASSIGNMENT: 'assignment',
  SUCCESS: 'success',
  ERROR: 'error',
};

const DetailsTab = () => {
  const dispatch = useDispatch();
  const { id } = useParams<{ id: string }>();

  const { facilityCustomers, isLoading } = useSelector(
    (state: RootState) => state.customers,
  );

  const { actionStatus: deviceActionStatus } = useSelector(
    (state: RootState) => state.devices,
  );

  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [assignStep, setAssignStep] = useState(ASSIGN_STEPS.ASSIGNMENT);

  const ownerDetails = (facilityCustomers || []).find(
    (customer) => customer.customer_id === Number(id),
  );

  useEffect(() => {
    if (!facilityCustomers || ownerDetails?.customer_id !== Number(id)) {
      dispatch(getCustomer(Number(id)));
    }
  }, [dispatch, facilityCustomers, ownerDetails?.customer_id, id]);

  const handleAssignClick = () => {
    setAssignStep(ASSIGN_STEPS.ASSIGNMENT);
    setIsAssignModalOpen(true);
  };

  const handleAssignClose = () => {
    setIsAssignModalOpen(false);
    setAssignStep(ASSIGN_STEPS.ASSIGNMENT);
    dispatch(clearDeviceActionStatus());
  };

  const handleSuccessClose = () => {
    handleAssignClose();
    dispatch(getCustomer(Number(id)));
  };

  const handleHubAssignment = (hubId: number) => {
    if (!ownerDetails?.customer_id) return;
    dispatch(linkDeviceToOwner(hubId, ownerDetails.customer_id));
  };

  useEffect(() => {
    if (!isAssignModalOpen) return;
    if (deviceActionStatus.type === DeviceActionType.LinkDeviceToOwner) {
      if (deviceActionStatus.success) {
        setAssignStep(ASSIGN_STEPS.SUCCESS);
      } else {
        setAssignStep(ASSIGN_STEPS.ERROR);
      }
    }
  }, [deviceActionStatus, isAssignModalOpen]);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  console.log({ ownerDetails });

  return (
    <Box py={2}>
      <Grid container spacing={3}>
        {/* Left column */}
        <Grid item xs={12} sm={2} sx={{ minWidth: '250px' }}>
          <Typography sx={baseStyle}>Hub status</Typography>
          <Typography sx={contentStyle}>
            {ownerDetails?.hub_connectivity_status
              ? ownerDetails.hub_connectivity_status.charAt(0).toUpperCase() +
                ownerDetails.hub_connectivity_status.slice(1)
              : 'N/A'}
          </Typography>

          <Typography sx={baseStyle}>Serial number</Typography>
          <Typography sx={contentStyle}>
            {ownerDetails?.device_serial_number ?? 'N/A'}
          </Typography>

          <Typography sx={baseStyle}>Model number</Typography>
          <Typography sx={contentStyle}>
            {ownerDetails?.device_model_number ?? 'N/A'}
          </Typography>

          <Typography sx={baseStyle}>Hardware version</Typography>
          <Typography sx={contentStyle}>
            {ownerDetails?.device_hardware_version ?? 'N/A'}
          </Typography>

          <Typography sx={baseStyle}>Software version</Typography>
          <Typography sx={contentStyle}>
            {ownerDetails?.device_software_version ?? 'N/A'}
          </Typography>

          <Typography sx={baseStyle}>Activation date</Typography>
          <Typography sx={contentStyle}>
            {ownerDetails?.device_synced_at
              ? parseDateToLocal(ownerDetails.device_synced_at, 'MMMM d, yyyy')
              : ownerDetails?.device_created_at
                ? parseDateToLocal(
                    ownerDetails.device_created_at,
                    'MMMM d, yyyy',
                  )
                : 'N/A'}
          </Typography>
          {!ownerDetails?.device_id && !ownerDetails?.device_serial_number && (
            <Button
              variant="contained"
              color="secondary"
              sx={{
                width: '132px',
                height: '40px',
                fontSize: '14px',
                fontWeight: 700,
                lineHeight: '18px',
                letterSpacing: '0.2px',
              }}
              onClick={handleAssignClick}
            >
              Assign Hub
            </Button>
          )}
        </Grid>

        {/* Right column */}
        <Grid item xs={12} sm={6}>
          <Typography sx={baseStyle}>Pharmacy name</Typography>
          <Typography sx={contentStyle}>
            {ownerDetails?.pharmacy_name ?? 'N/A'}
          </Typography>

          <Typography sx={baseStyle}>Pharmacy phone number</Typography>
          <Typography sx={contentStyle}>
            {ownerDetails?.pharmacy_phone ?? 'N/A'}
          </Typography>

          <Typography sx={baseStyle}>Pharmacy Address</Typography>
          <Typography sx={contentStyle}>
            {ownerDetails?.pharmacy_address1 ||
            ownerDetails?.pharmacy_city ||
            ownerDetails?.pharmacy_state ||
            ownerDetails?.pharmacy_zipcode ? (
              <>
                {ownerDetails?.pharmacy_address1 && (
                  <span>
                    {ownerDetails.pharmacy_address1}
                    <br />
                  </span>
                )}
                {(ownerDetails?.pharmacy_city ||
                  ownerDetails?.pharmacy_state ||
                  ownerDetails?.pharmacy_zipcode) && (
                  <span>
                    {ownerDetails?.pharmacy_city
                      ? ownerDetails.pharmacy_city
                      : ''}
                    {ownerDetails?.pharmacy_state
                      ? `, ${ownerDetails.pharmacy_state}`
                      : ''}
                    {ownerDetails?.pharmacy_zipcode
                      ? ` ${ownerDetails.pharmacy_zipcode}`
                      : ''}
                  </span>
                )}
              </>
            ) : (
              'N/A'
            )}
          </Typography>
        </Grid>
      </Grid>
      {isAssignModalOpen && (
        <NewModal
          showCloseX
          title="Assign Hub"
          onClose={handleAssignClose}
          maxWidth="xs"
        >
          {assignStep === ASSIGN_STEPS.ASSIGNMENT && (
            <HubAssignment
              onSubmit={handleHubAssignment}
              onBack={handleAssignClose}
              facilityId={ownerDetails?.facility_id ?? null}
              showProgressBar={false}
            />
          )}
          {assignStep === ASSIGN_STEPS.SUCCESS && (
            <StatusMessage
              icon={<UpdateSwSuccessIcon />}
              title="Hub assigned successfully!"
              onClose={handleSuccessClose}
            />
          )}
          {assignStep === ASSIGN_STEPS.ERROR && (
            <StatusMessage
              icon={<HubAssignFailedIcon />}
              title="Failed to assign Hub"
              message="Please try again or come back later."
              onClose={handleAssignClose}
              primaryButtonText="Try again"
            />
          )}
        </NewModal>
      )}
    </Box>
  );
};

export default DetailsTab;
